import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useTypedParams } from '@sorare/routing';
import { ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP_USERGROUP_USER } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { ChampionshipNoLineups } from '#src/components/championship/ChampionshipNoLineups/index.tsx';
import { ChampionshipLayout } from '#src/layouts/ChampionshipLayout/index.tsx';

import type {
  ChampionshipQuery,
  ChampionshipQueryVariables,
} from './__generated__/layout.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const CHAMPIONSHIP_QUERY = gql`
  query ChampionshipQuery($userGroupSlug: String!) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        ...ChampionshipLayout_so5UserGroup
      }
    }
  }
  ${ChampionshipLayout.fragments.so5UserGroup}
` as TypedDocumentNode<ChampionshipQuery, ChampionshipQueryVariables>;

export const loader = queryLoader<PageParams>(
  CHAMPIONSHIP_QUERY,
  ({ userGroup }) => ({
    userGroupSlug: userGroup,
  })
);

const PlayProChampionshipUserGroupLayout = () => {
  const { generateSportContextPath } = useSportContext();
  const { eventType } = useTypedParams<PageParams>();
  const { data } = useQueryLoader<ChampionshipQuery>();
  const isMobile = useIsMobile();

  return (
    <ChampionshipLayout
      so5UserGroup={data.so5.so5UserGroup}
      to={({ userGroup, user }) =>
        generateSportContextPath(
          ANY_SPORT_PLAY_EVENTTYPE_CHAMPIONSHIP_USERGROUP_USER,
          {
            params: {
              eventType: eventType.toLowerCase(),
              userGroup,
              user,
            },
          }
        )
      }
      additionalContent={
        !!isMobile && (
          <ChampionshipNoLineups
            userGroupSlug={data.so5.so5UserGroup?.slug}
            nbCards={0}
          />
        )
      }
    />
  );
};

export default PlayProChampionshipUserGroupLayout;
